import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useGetBookRequestsQuery } from "../api/bookRequestApi";
import {
  BookRequestStatus,
  BookRequestResponseType,
  BookRequestPagination,
  ApiBookRequest,
} from "../types/bookRequestTypes";
import Table from "../../../../core/common/dataTable/index";
import CommonSelect, { Option } from "../../../../core/common/commonSelect";
import { all_routes } from "../../../router/all_routes";
import { useSelector } from "react-redux";
import BookRequestModals from "../BookRequestModals";
import { selectUserRole } from "../../../../core/data/slice/authSlice";
import { showToast } from "../../../../core/utils/alert";

// Helper function to transform API response to client format
const transformBookRequest = (
  apiRequest: ApiBookRequest
): BookRequestResponseType => {
  return {
    id: apiRequest.id,
    student_id: apiRequest.student.id,
    student_name: apiRequest.student.name || "",
    studentid: apiRequest.student.profile.studentid,
    center_id: apiRequest.center.id,
    center_name: apiRequest.center.name,
    course: apiRequest.course.name,
    stage: apiRequest.stage.name,
    request_date: apiRequest.requested_on,
    status: apiRequest.status,
    approved_date:
      apiRequest.status === "approved"
        ? apiRequest.reviewed_on || undefined
        : undefined,
    approved_by:
      apiRequest.status === "approved"
        ? apiRequest.reviewed_by !== null
          ? apiRequest.reviewed_by
          : undefined
        : undefined,
    rejection_reason:
      apiRequest.status === "rejected"
        ? apiRequest.reviewer_note || undefined
        : undefined,
    rejected_date:
      apiRequest.status === "rejected"
        ? apiRequest.reviewed_on || undefined
        : undefined,
    completion_date:
      apiRequest.status === "completed"
        ? apiRequest.reviewed_on || undefined
        : undefined,
  };
};

const BookRequestList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<BookRequestStatus | "">("");
  const [courseFilter, setCourseFilter] = useState<string>("");
  const [stageFilter, setStageFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const [selectedRequestId, setSelectedRequestId] = useState<
    number | undefined
  >(undefined);

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const routes = all_routes;

  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";
  const isCenterAdmin = userRole === "centeradmin";

  // Add this state for reject reason
  const [rejectReason, setRejectReason] = useState("");

  // Build query parameters
  const queryParams: BookRequestPagination = {
    page: currentPage,
    size: recordsPerPage,
    search: searchTerm,
    status: statusFilter as BookRequestStatus | undefined,
    course: courseFilter || undefined,
    stage: stageFilter || undefined,
  };

  // Use RTK Query for API data
  const {
    data: bookRequestsData,
    isLoading: bookRequestLoading,
    error: bookRequestError,
    refetch: refetchBookRequests,
  } = useGetBookRequestsQuery(queryParams);

  // Transform API response to client format
  const bookRequests: BookRequestResponseType[] =
    bookRequestsData?.data?.items?.map((item) => transformBookRequest(item)) ||
    [];

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    refetchBookRequests();
  };

  const handleSearchChange = (text: string) => {
    setSearchTerm(text);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Page change handler
  const handlePageChange = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    if (pageSize) {
      setRecordsPerPage(pageSize);
    }
  };

  const handleDeleteClick = (id: number) => {
    setSelectedRequestId(id);
  };

  // Add this function to check payment status, similar to view-request.tsx
  const checkPaymentStatus = (bookrequest: ApiBookRequest | undefined) => {
    if (!bookrequest) {
      return { isValid: false, message: "Invalid request data" };
    }

    const center_payments = bookrequest.student?.center_payments || [];
    const stageName = bookrequest.stage?.name || "";

    // Safely extract stage number
    const stageNumberMatch = stageName.match(/\d+/);
    const stageNumber = stageNumberMatch ? parseInt(stageNumberMatch[0]) : 0;

    if (!center_payments || center_payments.length === 0) {
      if (stageName === "Level 1") {
        return {
          isValid: false,
          message: "Registration fee share for this student is pending",
        };
      } else {
        return {
          isValid: false,
          message: `Course fee share for stage ${stageNumber - 1
            } is pending for this student`,
        };
      }
    }

    if (stageName === "Level 1") {
      // For Stage 1, only check registration fee
      const registrationFee = center_payments.find(
        (payment) =>
          payment.fee_type === "registrationfee" &&
          payment.status === "completed"
      );
      if (!registrationFee) {
        return {
          isValid: false,
          message: "Registration fee share for this student is pending",
        };
      }
    } else {
      // For other stages, check previous stage's course fee
      const stageId = bookrequest.stage?.id;
      const previousStageId = stageId ? stageId - 1 : stageNumber - 1;
      const previousStageCourseFee = center_payments.find(
        (payment) =>
          payment.fee_type === "coursefee" &&
          payment.status === "completed" &&
          payment.stage_id !== null &&
          payment.stage_id === previousStageId
      );
      if (!previousStageCourseFee) {
        return {
          isValid: false,
          message: `Course fee share for previous stage is pending`,
        };
      }
    }

    return { isValid: true, message: "" };
  };

  // Update the handleApproveClick function to check payment status first
  const handleApproveClick = (id: number) => {
    // Find the book request by id
    const bookRequest = bookRequests.find((request) => request.id === id);
    if (!bookRequest) {
      showToast({
        title: "Warning",
        text: "Invalid request data",
        icon: "warning",
      });
      return;
    }

    // Find the original API request to check payment status
    const originalRequest = bookRequestsData?.data?.items?.find(
      (item) => item.id === id
    );

    const paymentStatus = checkPaymentStatus(originalRequest);
    if (!paymentStatus.isValid) {
      showToast({
        title: "Warning",
        text: paymentStatus.message || "Payment verification failed",
        icon: "warning",
      });
      return;
    }

    setSelectedRequestId(id);
    // The modal will be shown via Bootstrap data attributes
    // We don't need to manually show it since it's triggered by data-bs-target
  };

  const handleRejectClick = (id: number) => {
    setSelectedRequestId(id);
    setRejectReason("");
    // The modal will be shown via Bootstrap data attributes
  };

  const handleCompleteClick = (id: number) => {
    setSelectedRequestId(id);
    // The modal will be shown via Bootstrap data attributes
  };

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "completed", label: "Completed" },
  ];

  const courseOptions = [
    { value: "", label: "All Courses" },
    { value: "1", label: "Mental Arthemetic" },
    { value: "2", label: "Tiny Tots" },
  ];

  // Dynamic stage options based on selected course
  const getStageOptions = () => {
    const baseOptions = [{ value: "", label: "All Stages" }];

    if (!courseFilter || courseFilter === "") {
      return baseOptions;
    }

    if (courseFilter === "1") {
      // Mental Arithmetic has 8 stages
      return [
        ...baseOptions,
        ...Array.from({ length: 8 }, (_, i) => ({
          value: (i + 1).toString(),
          label: `Stage ${i + 1}`,
        })),
      ];
    } else if (courseFilter === "2") {
      // Tiny Tots has 10 stages
      return [
        ...baseOptions,
        ...Array.from({ length: 10 }, (_, i) => ({
          value: (i + 1).toString(),
          label: `Stage ${i + 1}`,
        })),
      ];
    }

    return baseOptions;
  };

  // Reset stage filter when course changes
  useEffect(() => {
    setStageFilter("");
  }, [courseFilter]);

  // Table columns configuration - conditionally include center column based on role
  const getColumns = () => {
    const baseColumns = [
      {
        title: "Student ID",
        dataIndex: "studentid",
        render: (text: string, record: BookRequestResponseType) => (
          <Link
            to={routes.viewBookRequest.replace(":id", record.id.toString())}
            className="link-primary"
          >
            {text}
          </Link>
        ),
        sorter: (a: BookRequestResponseType, b: BookRequestResponseType) =>
          a.studentid?.localeCompare(b.studentid || "") || 0,
      },
      {
        title: "Student Name",
        dataIndex: "student_name",
        render: (text: string, record: BookRequestResponseType) => (
          <Link
            to={routes.viewBookRequest.replace(":id", record.id.toString())}
            className="link-primary"
          >
            {text}
          </Link>
        ),
        sorter: (a: BookRequestResponseType, b: BookRequestResponseType) =>
          a.student_name.localeCompare(b.student_name),
      },
      {
        title: "Course",
        dataIndex: "course",
        render: (text: string) => text,
        sorter: (a: BookRequestResponseType, b: BookRequestResponseType) =>
          a.course.localeCompare(b.course),
      },
      {
        title: "Level",
        dataIndex: "level",
        sorter: (a: BookRequestResponseType, b: BookRequestResponseType) =>
          Number(a.stage) - Number(b.stage),
      },
      {
        title: "Request Date",
        dataIndex: "request_date",
        sorter: (a: BookRequestResponseType, b: BookRequestResponseType) =>
          new Date(a.request_date).getTime() -
          new Date(b.request_date).getTime(),
      },
      // Center column will be inserted here for superadmin
      {
        title: "Status",
        dataIndex: "status",
        render: (status: BookRequestStatus) => {
          let colorClass = "";

          switch (status) {
            case "pending":
              colorClass = "warning";
              break;
            case "approved":
              colorClass = "success";
              break;
            case "rejected":
              colorClass = "danger";
              break;
            case "completed":
              colorClass = "info";
              break;
            default:
              colorClass = "secondary";
          }

          return (
            <span
              className={`badge badge-soft-${colorClass} d-inline-flex align-items-center`}
            >
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {status}
            </span>
          );
        },
        sorter: (a: BookRequestResponseType, b: BookRequestResponseType) =>
          a.status.localeCompare(b.status),
      },
      {
        title: "Action",
        render: (record: BookRequestResponseType) => {
          // Find the original API request to check payment status
          const originalRequest = bookRequestsData?.data?.items?.find(
            (item) => item.id === record.id
          );
          const paymentStatus = checkPaymentStatus(originalRequest);
          const isPaymentValid = paymentStatus.isValid;

          return (
            <div className="d-flex align-items-center">
              <div className="dropdown">
                <Link
                  to="#"
                  className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="ti ti-dots-vertical fs-14" />
                </Link>
                <ul className="dropdown-menu dropdown-menu-right p-3">
                  <li>
                    <Link
                      className="dropdown-item rounded-1"
                      to={routes.viewBookRequest.replace(
                        ":id",
                        record.id.toString()
                      )}
                    >
                      <i className="ti ti-eye me-2" />
                      View Details
                    </Link>
                  </li>

                  {isSuperAdmin && record.status === "pending" && (
                    <>
                      <li>
                        {isPaymentValid ? (
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            data-bs-toggle="modal"
                            data-bs-target="#approve-modal"
                            onClick={(e) => {
                              e.preventDefault();
                              handleApproveClick(record.id);
                            }}
                          >
                            <i className="ti ti-check me-2" />
                            Approve
                          </Link>
                        ) : (
                          <Link
                            to="#"
                            className="dropdown-item rounded-1 text-muted"
                            title={
                              paymentStatus.message ||
                              "Payment verification failed"
                            }
                          >
                            <i className="ti ti-check me-2" />
                            Approve
                            <div className="small text-warning mt-1">
                              {"Claims are pending"}
                            </div>
                          </Link>
                        )}
                      </li>
                      <li>
                        <Link
                          to="#"
                          className="dropdown-item rounded-1"
                          data-bs-toggle="modal"
                          data-bs-target="#reject-modal"
                          onClick={(e) => {
                            e.preventDefault();
                            handleRejectClick(record.id);
                          }}
                        >
                          <i className="ti ti-x me-2" />
                          Reject
                        </Link>
                      </li>
                    </>
                  )}

                  {isCenterAdmin && record.status === "approved" && (
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        data-bs-toggle="modal"
                        data-bs-target="#complete-modal"
                        onClick={(e) => {
                          e.preventDefault();
                          handleCompleteClick(record.id);
                        }}
                      >
                        <i className="feather-check-square me-2" />
                        Mark as Delivered
                      </Link>
                    </li>
                  )}

                  {isCenterAdmin && record.status === "pending" && (
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        data-bs-toggle="modal"
                        data-bs-target="#delete-modal"
                        onClick={() => handleDeleteClick(record.id)}
                      >
                        <i className="ti ti-trash me-2" />
                        Delete
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          );
        },
      },
    ];

    // Only add center column for superadmin
    if (isSuperAdmin) {
      baseColumns.splice(5, 0, {
        title: "Center",
        dataIndex: "center_name",
        sorter: (a: BookRequestResponseType, b: BookRequestResponseType) =>
          a.center_name?.localeCompare(b.center_name || "") || 0,
      });
    }

    return baseColumns;
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Level Book Requests</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.centerDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">
                    Level Book Requests
                  </li>
                </ol>
              </nav>
            </div>
            {isCenterAdmin && (
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                <div className="mb-2">
                  <Link
                    to={routes.bulkBookRequest}
                    className="btn btn-primary d-flex align-items-center"
                  >
                    <i className="ti ti-square-rounded-plus me-2" />
                    Bulk Book Request
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Request List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Book Request List</h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* Filters remain the same... */}
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Course</label>
                              <CommonSelect
                                className="select"
                                options={courseOptions}
                                onChange={(option: Option | null) =>
                                  setCourseFilter(option?.value || "")
                                }
                                value={
                                  courseOptions.find(
                                    (option) => option.value === courseFilter
                                  ) || null
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Level</label>
                              <CommonSelect
                                className="select"
                                options={getStageOptions()}
                                onChange={(option: Option | null) =>
                                  setStageFilter(option?.value || "")
                                }
                                value={
                                  getStageOptions().find(
                                    (option) => option.value === stageFilter
                                  ) || null
                                }
                                isDisabled={!courseFilter}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={statusOptions}
                                onChange={(option: Option | null) =>
                                  setStatusFilter(
                                    (option?.value as BookRequestStatus) || ""
                                  )
                                }
                                value={
                                  statusOptions.find(
                                    (option) => option.value === statusFilter
                                  ) || null
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={() => {
                            setStatusFilter("");
                            setCourseFilter("");
                            setStageFilter("");
                            setSearchTerm("");
                          }}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Sort By Dropdown */}
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by A-Z
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1 active">
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Recently Requested
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {bookRequestLoading ? (
                <div className="text-center">Loading book requests...</div>
              ) : bookRequestError ? (
                <div className="alert alert-danger m-3">
                  Error loading book requests: {bookRequestError.toString()}
                </div>
              ) : (
                <Table
                  dataSource={bookRequests}
                  columns={getColumns()}
                  Selection={false}
                  onSearchChange={handleSearchChange}
                  pagination={{
                    current: currentPage,
                    pageSize: recordsPerPage,
                    total: bookRequestsData?.data?.totalItems || 0,
                    onChange: handlePageChange,
                    pageSizeOptions: ["All", 10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <BookRequestModals
        requestId={selectedRequestId}
        onSuccess={() => {
          // Refresh the book requests list
          refetchBookRequests();
        }}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
      />
    </>
  );
};

export default BookRequestList;
